import { Link } from "react-router";
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconCircleCheck,
  IconDevices,
  type IconProps,
} from "@tabler/icons-react";
import type { ComponentType } from "react";

import { cn } from "@/lib/cn";

type Emphasis = "neutral" | "ok" | "warn" | "danger";

const EMPHASIS: Record<Emphasis, string> = {
  neutral: "text-ink",
  ok: "text-ok",
  warn: "text-warn",
  danger: "text-danger",
};

const EMPHASIS_ICON: Record<Emphasis, ComponentType<IconProps>> = {
  neutral: IconDevices,
  ok: IconCircleCheck,
  warn: IconAlertTriangle,
  danger: IconAlertCircle,
};

interface KpiCardProps {
  label: string;
  value: number;
  emphasis?: Emphasis;
  hint?: string;
  /** Turns the card into a link to the list it summarizes. */
  to?: string;
  icon?: ComponentType<IconProps>;
}

/**
 * A number is only useful if it leads somewhere: "3 not reporting" should take
 * you to those three. Cards without a matching filter stay plain text rather
 * than pretending to be clickable.
 */
export function KpiCard({
  label,
  value,
  emphasis = "neutral",
  hint,
  to,
  icon,
}: KpiCardProps) {
  const Icon = icon ?? EMPHASIS_ICON[emphasis];

  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-ink-muted">{label}</p>
        <Icon size={22} stroke={1.75} className={cn("shrink-0", EMPHASIS[emphasis])} aria-hidden />
      </div>
      <p className={cn("mt-2 text-3xl font-semibold tabular-nums tracking-tight", EMPHASIS[emphasis])}>
        {value}
      </p>
      {hint ? <p className="mt-1 text-xs text-ink-muted">{hint}</p> : null}
    </>
  );

  const shell = "rounded-md border border-border-subtle bg-surface px-4 py-4 shadow-sm";

  if (!to) {
    return <div className={shell}>{body}</div>;
  }

  return (
    <Link to={to} className={cn(shell, "block transition-colors hover:border-brand/40")}>
      {body}
    </Link>
  );
}
